import { useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Divider,
  useTheme,
  useMediaQuery,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SearchAndReplaceProps } from './types';
import { useSearchState } from './hooks/useSearchState';
import { useSearchReplace } from './hooks/useSearchReplace';
import { CheckboxOption } from './components/CheckboxOption';
import { SearchField } from './components/SearchField';
import { createStyles } from './utils/styles';
import { BUTTON_CONFIG } from './utils/constants';

const SearchAndReplaceMenu = ({ editor, open, onClose }: SearchAndReplaceProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Custom hooks
  const { searchState, handleInputChange, handleCheckboxChange, resetSearchTerms } =
    useSearchState();

  const { actionHandlers, resultIndex, resultsLength } = useSearchReplace(
    editor,
    searchState,
    resetSearchTerms,
    open,
  );

  // Destructure for convenience
  const { searchTerm, replaceTerm, caseSensitive, wholeWord } = searchState;

  // Styles
  const styles = createStyles({ isMobile, theme });

  // Handle Escape key to close the menu
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      //Close on Escape, navigate results on Enter
      if (event.key === 'Escape' && open) {
        event.preventDefault();
        onClose();
      }
      // Only handle Enter from outside the search menu (SearchField handles its own Enter/Shift+Enter)
      const menuEl = document.getElementById('search-and-replace-menu');
      if (event.key === 'Enter' && open && !menuEl?.contains(event.target as Node)) {
        event.preventDefault();
        if (event.shiftKey) {
          actionHandlers.previous();
        } else {
          actionHandlers.next();
        }
      }
    },
    [open, onClose, actionHandlers],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, handleKeyDown]);

  return (
    <Box
      id="search-and-replace-menu"
      data-testid="search-and-replace-menu"
      sx={{ ...styles.container, display: open ? 'flex' : 'none' }}
    >
      <Stack
        spacing={1.5}
        sx={styles.mainStack}
      >
        <Tooltip title={t('blox.actions.close')}>
          <IconButton
            onClick={onClose}
            aria-label={t('blox.actions.close')}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
        {/* Search and Replace Fields */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={styles.inputFields}
        >
          <SearchField
            data-testid="find-replace-find-term-input"
            id="find-replace-find-term"
            label={t('blox.editorial.toolbar.search_replace.find.label')}
            value={searchTerm}
            onChange={(value: string) =>
              handleInputChange('searchTerm')({ target: { value } } as any)
            }
            onEnterPress={actionHandlers.next}
            onShiftEnterPress={actionHandlers.previous}
            placeholder={t('blox.editorial.toolbar.search_replace.find.placeholder')}
            autoFocus={open}
          />

          <SearchField
            data-testid="find-replace-replace-term-input"
            id="find-replace-replace-term"
            label={t('blox.editorial.toolbar.search_replace.replace.label')}
            value={replaceTerm}
            onChange={(value: string) =>
              handleInputChange('replaceTerm')({ target: { value } } as any)
            }
            onEnterPress={actionHandlers.replace}
            placeholder={t('blox.editorial.toolbar.search_replace.replace.placeholder')}
          />
        </Stack>

        {/* Options and Actions Row */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 1, md: 2 }}
          sx={styles.optionsRow}
        >
          {/* Checkboxes */}
          <Stack
            direction="row"
            spacing={2}
            sx={styles.checkboxes}
          >
            <CheckboxOption
              id="match-case"
              checked={caseSensitive}
              onChange={handleCheckboxChange('caseSensitive')}
              label={t('blox.editorial.toolbar.search_replace.match_case')}
            />

            <CheckboxOption
              id="match-whole-word"
              checked={wholeWord}
              onChange={handleCheckboxChange('wholeWord')}
              label={t('blox.editorial.toolbar.search_replace.match_whole_word')}
            />
          </Stack>

          {/* Action Buttons and Results */}
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={styles.actionButtonsContainer}
          >
            {/* Buttons */}

            {BUTTON_CONFIG.map((btn) => {
              const label = btn?.label ? t(btn.label) : '';

              if (btn.action === 'pipe') {
                return (
                  <Divider
                    key={btn.action}
                    orientation="vertical"
                    flexItem
                  />
                );
              }

              return (
                <Button
                  data-testid={btn.testId}
                  key={btn.action}
                  onClick={actionHandlers[btn.action]}
                  sx={styles.button}
                  variant="contained"
                  size="small"
                  disableElevation
                >
                  {label}
                </Button>
              );
            })}

            {/* Reset Button */}

            {/* Results Counter */}
            <Typography sx={styles.resultsCounter}>
              {t('blox.editorial.toolbar.search_replace.results', {
                current: resultsLength > 0 ? resultIndex + 1 : 0,
                total: resultsLength,
              })}
            </Typography>
          </Stack>
        </Stack>
      </Stack>
    </Box>
  );
};

export default SearchAndReplaceMenu;
